import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import {
    ArrowClockwise,
    Building,
    CheckCircleFill,
    CloudArrowUpFill,
    FileEarmarkTextFill,
    FolderFill,
    RocketTakeoffFill,
} from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { createOrganization } from '../api/organizations';
import { fetchQuotaStatus, QuotaStatus } from '../api/stripe_billing';
import { getGuidedTourStepIndexById } from '../tour/dashboardGuidedTourConfig';
import { markOnboardingOrganizationCreated } from '../tour/guidedTourStorage';
import { ProjectType, ProjectWithRepository } from '../types/project';
import { RequirementDocument, RequirementGenerationJob } from '../types/requirements';
import { userHasOrganization } from './setPasswordFlow';
import { projectTestCasesRoute } from '../utils/routes';
import { formatApiError } from '../utils/apiError';
import './auth-shared.css';
import './ProjectCreate-new.css';

type OnboardingStep = 1 | 2 | 3 | 4;

type MessageTone = 'success' | 'danger' | 'info';

interface InlineMessage
{
    tone: MessageTone;
    text: string;
}

interface AzureIconProps
{
    fill: string;
}

interface ProjectTypeCardProps
{
    selected: boolean;
    onSelect: () => void;
    label: string;
    icon: React.ReactNode;
    cardId: string;
}

const REQUIREMENT_ACCEPTED_TYPES = '.txt,.md,.markdown,.csv,.pdf,.docx,.xlsx';
const REQUIREMENT_ACCEPTED_EXTENSIONS = new Set(['.txt', '.md', '.markdown', '.csv', '.pdf', '.docx', '.xlsx']);
const REQUIREMENT_MAX_FILE_BYTES = 25 * 1024 * 1024;
const DOCUMENT_POLL_INTERVAL_MS = 4000;
const JOB_POLL_INTERVAL_MS = 4000;

interface TutorialCoachmark
{
    step: OnboardingStep;
    targetId: string;
    title: string;
    body: string;
    advanceRequiresSourceUpload?: boolean;
}

interface TutorialPosition
{
    top: number;
    left: number;
    arrowLeft: number;
    placement: 'top' | 'bottom';
}

const ONBOARDING_COACHMARKS: TutorialCoachmark[] = [
    {
        step: 1,
        targetId: 'onboarding-org-name',
        title: 'Start with your organization',
        body: 'This is the workspace root for everything that follows. Name the organization first so the rest of onboarding can attach to a real account context.',
    },
    {
        step: 1,
        targetId: 'onboarding-workspace-continue',
        title: 'Continue to billing',
        body: 'Once the organization looks right, continue to review the free quota path and optional subscription setup.',
    },
    {
        step: 2,
        targetId: 'onboarding-project-name',
        title: 'Name the first project',
        body: 'This becomes the project workspace used by document upload, repository context, and the first generated test-case run.',
    },
    {
        step: 2,
        targetId: 'onboarding-domain-description',
        title: 'Describe the domain',
        body: 'Add product and workflow context here so generated artifacts match the language and behaviors your team actually uses.',
    },
    {
        step: 2,
        targetId: 'onboarding-project-type',
        title: 'Choose the project source model',
        body: 'Pick a simple project for document-first onboarding, or Azure DevOps when the project should be tied directly to Azure work items and repository context.',
    },
    {
        step: 2,
        targetId: 'onboarding-azure-org',
        title: 'Set the Azure DevOps organization',
        body: 'Start with the Azure DevOps organization so the project can map work items and repositories to the correct tenant.',
    },
    {
        step: 2,
        targetId: 'onboarding-azure-project',
        title: 'Choose the Azure DevOps project',
        body: 'Point onboarding to the Azure project that contains the work items, repositories, and boards this project should use.',
    },
    {
        step: 2,
        targetId: 'onboarding-azure-team',
        title: 'Choose the Azure DevOps team',
        body: 'Use the team value that owns the backlog and boards you want this onboarding flow to reference.',
    },
    {
        step: 2,
        targetId: 'onboarding-azure-pat',
        title: 'Add the Azure DevOps access token',
        body: 'Provide the personal access token last so onboarding can authenticate to Azure DevOps once the rest of the mapping is defined.',
    },
    {
        step: 2,
        targetId: 'onboarding-project-continue',
        title: 'Create the project record',
        body: 'This saves the project shell so the next onboarding step can attach source material and prepare the first generation job.',
    },
    {
        step: 3,
        targetId: 'onboarding-document-files',
        title: 'Upload requirement sources',
        body: 'Select requirement documents here first. They are the primary inputs used for onboarding and they begin extracting as soon as they upload.',
    },
    {
        step: 3,
        targetId: 'onboarding-document-notes',
        title: 'Add context for the files',
        body: 'Use notes to explain what the uploaded files represent. This helps preserve intent when the source material needs extra guidance.',
    },
    {
        step: 3,
        targetId: 'onboarding-document-upload',
        title: 'Upload the selected documents',
        body: 'Use this action after selecting files and notes. The next tutorial tip stays locked until a requirement document has actually been uploaded.',
        advanceRequiresSourceUpload: true,
    },
    {
        step: 3,
        targetId: 'onboarding-sources-continue',
        title: 'Continue when at least one file is uploaded',
        body: 'Once requirement material exists in the project, use this action to move into the first-run screen and queue onboarding output.',
    },
    {
        step: 4,
        targetId: 'onboarding-starter-quota',
        title: 'Review the starter quota',
        body: 'This panel confirms the free balance available to the organization before you launch the first onboarding job.',
    },
    {
        step: 4,
        targetId: 'onboarding-run-requirements-option',
        title: 'Choose extracted requirements',
        body: 'Keep this enabled when the first run should generate extracted requirements from the ready source documents.',
    },
    {
        step: 4,
        targetId: 'onboarding-run-testcases-option',
        title: 'Choose structured test cases',
        body: 'Enable this option when the first run should also generate structured test cases alongside requirements.',
    },
    {
        step: 4,
        targetId: 'onboarding-run-job',
        title: 'Launch the first generation run',
        body: 'This is the final onboarding action. It queues Test Case Creation against the documents that are ready right now.',
    },
];

function AzureDevOpsIcon({ fill }: AzureIconProps): React.ReactElement
{
    return (
        <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M0 13.186l3.183-4.103V4.648L8.6 2.722h.005L15 7.974V2.048L18.23 1.5 24 10.253l-2.726 1.142v8.007L14.877 22.5l-7.636-2.779-.454 2.396-3.637-.397-.363-5.216ZM14.877 19.98V8.7L9.422 6.25 6.694 13.126Z"
                fill={fill}
                fillRule="evenodd"
                clipRule="evenodd"
            />
        </svg>
    );
}

function ProjectTypeCard(
    { selected, onSelect, label, icon, cardId }: ProjectTypeCardProps,
): React.ReactElement
{
    return (
        <button
            type="button"
            id={cardId}
            className={`project-type-card${selected ? ' project-type-card-selected' : ''}`}
            onClick={onSelect}
        >
            <div className="project-type-card-indicator">
                {selected
                    ? <CheckCircleFill size={18} className="project-type-card-check" />
                    : <div className="project-type-card-circle" />
                }
            </div>

            <div className="project-type-card-icon">{icon}</div>
            <div className="project-type-card-label">{label}</div>
        </button>
    );
}

function formatFileSize(bytes: number): string
{
    if (bytes < 1024)
    {
        return `${bytes} B`;
    }

    const units = ['KB', 'MB', 'GB'];
    let value = bytes / 1024;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1)
    {
        value /= 1024;
        unitIndex += 1;
    }

    const digits = value >= 10 ? 0 : 1;
    return `${value.toFixed(digits)} ${units[unitIndex]}`;
}

function formatTokenCount(value?: number | null): string
{
    if (value == null)
    {
        return '—';
    }

    return value.toLocaleString();
}

function isDocumentPending(document: RequirementDocument): boolean
{
    return document.status === 'uploaded' || document.status === 'extracting';
}

function isJobActive(job: RequirementGenerationJob | null): boolean
{
    const status = job?.status?.toLowerCase();
    return status === 'pending' || status === 'running';
}

function getDocumentStatusClass(status: RequirementDocument['status']): string
{
    switch (status)
    {
        case 'ready':
            return 'ps-status-pill ps-status-pill--ready';
        case 'failed':
            return 'ps-status-pill ps-status-pill--failed';
        case 'extracting':
            return 'ps-status-pill ps-status-pill--extracting';
        default:
            return 'ps-status-pill ps-status-pill--uploaded';
    }
}

function getJobStatusClass(status: string): string
{
    const lower = status.toLowerCase();

    if (lower === 'completed' || lower === 'success')
    {
        return 'ps-status-pill ps-status-pill--ready';
    }

    if (lower === 'failed' || lower === 'error')
    {
        return 'ps-status-pill ps-status-pill--failed';
    }

    return 'ps-status-pill ps-status-pill--extracting';
}

function resolveJobProgress(job: RequirementGenerationJob): number
{
    const lower = job.status.toLowerCase();

    if (lower === 'completed' || lower === 'success')
    {
        return 100;
    }

    if (lower === 'failed' || lower === 'error')
    {
        return Math.max(job.progress || 0, 12);
    }

    return Math.max(job.progress || 0, lower === 'running' ? 18 : 8);
}

function getMessageClassName(tone: MessageTone): string
{
    if (tone === 'danger')
    {
        return 'auth-server-error';
    }

    if (tone === 'success')
    {
        return 'auth-server-success';
    }

    return 'auth-server-info';
}

/* ---------- Main component ---------- */

const ProfileSetupNew: React.FC = () =>
{
    const navigate = useNavigate();
    const { user, loading: authLoading, updateUser } = useAuth();
    const
    {
        createProject,
        updateProject,
        fetchRequirementDocuments,
        uploadRequirementDocuments,
        queueRequirementGeneration,
        fetchRequirementGeneration,
    } = useProjects();

    const hasCheckedInitialRedirect = useRef(false);
    const tutorialBannerRef = useRef<HTMLDivElement | null>(null);
    const tutorialArrowRef = useRef<HTMLDivElement | null>(null);

    const [step, setStep] = useState<OnboardingStep>(1);
    const [tutorialVisible, setTutorialVisible] = useState<boolean>(true);
    const [tutorialIndexes, setTutorialIndexes] = useState<Record<OnboardingStep, number>>({
        1: 0,
        2: 0,
        3: 0,
        4: 0,
    });
    const [tutorialPosition, setTutorialPosition] = useState<TutorialPosition | null>(null);

    const [orgName, setOrgName] = useState<string>('');
    const [workspaceSubmitted, setWorkspaceSubmitted] = useState<boolean>(false);
    const [workspaceSaving, setWorkspaceSaving] = useState<boolean>(false);
    const [workspaceError, setWorkspaceError] = useState<string | null>(null);

    const [projectName, setProjectName] = useState<string>('');
    const [domainDescription, setDomainDescription] = useState<string>('');
    const [projectType, setProjectType] = useState<ProjectType>('simple');
    const [azureOrg, setAzureOrg] = useState<string>('');
    const [azureProject, setAzureProject] = useState<string>('');
    const [azureTeam, setAzureTeam] = useState<string>('');
    const [azurePAT, setAzurePAT] = useState<string>('');
    const [projectSubmitted, setProjectSubmitted] = useState<boolean>(false);
    const [projectSaving, setProjectSaving] = useState<boolean>(false);
    const [projectError, setProjectError] = useState<string | null>(null);

    const [createdOrganizationId, setCreatedOrganizationId] = useState<number | null>(null);
    const [createdProject, setCreatedProject] = useState<ProjectWithRepository | null>(null);

    const [selectedRequirementFiles, setSelectedRequirementFiles] = useState<File[]>([]);
    const [documentNotes, setDocumentNotes] = useState<string>('');
    const [documents, setDocuments] = useState<RequirementDocument[]>([]);
    const [documentsLoading, setDocumentsLoading] = useState<boolean>(false);
    const [uploadingDocuments, setUploadingDocuments] = useState<boolean>(false);
    const [documentFeedback, setDocumentFeedback] = useState<InlineMessage | null>(null);
    const [sourceError, setSourceError] = useState<string | null>(null);

    const [quota, setQuota] = useState<QuotaStatus | null>(null);
    const [quotaLoading, setQuotaLoading] = useState<boolean>(false);
    const [quotaError, setQuotaError] = useState<string | null>(null);
    const [includeRequirements, setIncludeRequirements] = useState<boolean>(true);
    const [includeTestCases, setIncludeTestCases] = useState<boolean>(true);
    const [job, setJob] = useState<RequirementGenerationJob | null>(null);
    const [jobSubmitting, setJobSubmitting] = useState<boolean>(false);
    const [jobFeedback, setJobFeedback] = useState<InlineMessage | null>(null);
    const [jobError, setJobError] = useState<string | null>(null);

    useEffect(() =>
    {
        if (authLoading || hasCheckedInitialRedirect.current)
        {
            return;
        }

        hasCheckedInitialRedirect.current = true;

        if (userHasOrganization(user))
        {
            navigate('/dashboard', { replace: true });
        }
    }, [authLoading, navigate, user]);

    const firstName = (user?.name || '').split(' ')[0] || 'there';
    const readyDocuments = useMemo(
        () => documents.filter((document) => document.status === 'ready'),
        [documents],
    );
    const pendingDocuments = useMemo(
        () => documents.filter((document) => isDocumentPending(document)),
        [documents],
    );
    const failedDocuments = useMemo(
        () => documents.filter((document) => document.status === 'failed'),
        [documents],
    );
    const hasUploadedSource = documents.length > 0;
    const currentCoachmarks = useMemo(
        () => ONBOARDING_COACHMARKS.filter((coachmark) =>
        {
            if (coachmark.step !== step)
            {
                return false;
            }

            if (
                coachmark.targetId === 'onboarding-azure-org' ||
                coachmark.targetId === 'onboarding-azure-project' ||
                coachmark.targetId === 'onboarding-azure-team' ||
                coachmark.targetId === 'onboarding-azure-pat'
            )
            {
                return projectType === 'azure_devops';
            }

            return true;
        }),
        [projectType, step],
    );
    const currentTutorialIndex = currentCoachmarks.length === 0
        ? 0
        : Math.min(tutorialIndexes[step], currentCoachmarks.length - 1);
    const activeCoachmark = tutorialVisible && currentCoachmarks.length > 0
        ? currentCoachmarks[currentTutorialIndex]
        : null;
    const isFirstCoachmark = currentTutorialIndex === 0;
    const isLastCoachmark = currentCoachmarks.length === 0
        ? true
        : currentTutorialIndex === currentCoachmarks.length - 1;
    const shouldHideNextTutorialTip = Boolean(activeCoachmark?.advanceRequiresSourceUpload && !hasUploadedSource);
    const isTutorialUploadBlocked = uploadingDocuments;
    const tutorialBlockMessage = shouldHideNextTutorialTip
        ? 'Upload a requirement document to unlock the next tip.'
        : uploadingDocuments
            ? 'Wait until the current document upload finishes before moving to the next tip.'
            : null;

    function isTutorialTargetActive(targetIds: string | string[]): boolean
    {
        const ids = Array.isArray(targetIds) ? targetIds : [targetIds];
        return Boolean(activeCoachmark && ids.includes(activeCoachmark.targetId));
    }

    function updateCoachmarkIndex(nextIndex: number): void
    {
        setTutorialIndexes((current) =>
        ({
            ...current,
            [step]: Math.max(0, Math.min(nextIndex, Math.max(currentCoachmarks.length - 1, 0))),
        }));
    }

    function getTutorialTargetClassName(targetIds: string | string[], className = ''): string
    {
        const classes = [className, 'ps-tutorial-target'];

        if (isTutorialTargetActive(targetIds))
        {
            classes.push('ps-tutorial-target--active');
        }

        return classes.filter(Boolean).join(' ');
    }

    const loadProjectDocuments = useCallback(
        async (projectId: number, options?: { silent?: boolean }): Promise<void> =>
        {
            if (!options?.silent)
            {
                setDocumentsLoading(true);
            }

            try
            {
                const nextDocuments = await fetchRequirementDocuments(projectId);
                setDocuments(nextDocuments);
                setSourceError(null);
            }
            catch (err: any)
            {
                if (!options?.silent)
                {
                    const detail =
                        err?.response?.data?.detail ||
                        err?.message ||
                        'Failed to load requirement documents.';
                    setSourceError(detail);
                }
            }
            finally
            {
                if (!options?.silent)
                {
                    setDocumentsLoading(false);
                }
            }
        },
        [fetchRequirementDocuments],
    );

    const loadQuota = useCallback(
        async (options?: { silent?: boolean }): Promise<void> =>
        {
            if (!options?.silent)
            {
                setQuotaLoading(true);
            }

            try
            {
                const nextQuota = await fetchQuotaStatus();
                setQuota(nextQuota);
                setQuotaError(null);
            }
            catch (err: any)
            {
                const detail =
                    err?.response?.data?.detail ||
                    err?.message ||
                    'Free quota details are unavailable right now.';
                setQuotaError(detail);
            }
            finally
            {
                if (!options?.silent)
                {
                    setQuotaLoading(false);
                }
            }
        },
        [],
    );

    useEffect(
        () =>
        {
            if (!createdProject?.id || (step !== 3 && step !== 4))
            {
                return;
            }

            void loadProjectDocuments(createdProject.id);
        },
        [createdProject?.id, loadProjectDocuments, step],
    );

    useEffect(
        () =>
        {
            if (!createdProject?.id || (step !== 3 && step !== 4))
            {
                return;
            }

            if (!documents.some((document) => isDocumentPending(document)))
            {
                return;
            }

            const intervalId = window.setInterval(() =>
            {
                void loadProjectDocuments(createdProject.id, { silent: true });
            }, DOCUMENT_POLL_INTERVAL_MS);

            return () => window.clearInterval(intervalId);
        },
        [createdProject?.id, documents, loadProjectDocuments, step],
    );

    useEffect(
        () =>
        {
            if (step !== 4 || !createdOrganizationId)
            {
                return;
            }

            void loadQuota();
        },
        [createdOrganizationId, loadQuota, step],
    );

    useEffect(
        () =>
        {
            if (!createdProject?.id || !job?.id || !isJobActive(job))
            {
                return;
            }

            const intervalId = window.setInterval(async () =>
            {
                try
                {
                    const nextJob = await fetchRequirementGeneration(createdProject.id, job.id);
                    setJob(nextJob);

                    if (!isJobActive(nextJob))
                    {
                        void loadQuota({ silent: true });
                    }
                }
                catch
                {
                    /* keep the last known status visible */
                }
            }, JOB_POLL_INTERVAL_MS);

            return () => window.clearInterval(intervalId);
        },
        [createdProject?.id, fetchRequirementGeneration, job, loadQuota],
    );

    useEffect(
        () =>
        {
            if (!activeCoachmark)
            {
                setTutorialPosition(null);
                return;
            }

            const targetElement = document.getElementById(activeCoachmark.targetId);
            const bannerElement = tutorialBannerRef.current;

            if (bannerElement)
            {
                bannerElement.style.visibility = 'hidden';
            }

            if (!targetElement)
            {
                setTutorialPosition(null);
                return;
            }

            targetElement.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });

            let animationFrameId = 0;

            const focusTarget = (element: HTMLElement): void =>
            {
                const focusableSelectors = [
                    'input:not([type="hidden"]):not([disabled])',
                    'textarea:not([disabled])',
                    'select:not([disabled])',
                    'button:not([disabled])',
                    '[tabindex]:not([tabindex="-1"])',
                ].join(', ');

                const focusCandidate = element.matches(focusableSelectors)
                    ? element
                    : element.querySelector<HTMLElement>(focusableSelectors);

                if (!focusCandidate || typeof focusCandidate.focus !== 'function')
                {
                    return;
                }

                window.requestAnimationFrame(() =>
                {
                    focusCandidate.focus({ preventScroll: true });

                    if (focusCandidate instanceof HTMLTextAreaElement)
                    {
                        const textLength = focusCandidate.value.length;
                        focusCandidate.setSelectionRange(textLength, textLength);
                        return;
                    }

                    if (focusCandidate instanceof HTMLInputElement)
                    {
                        const textSelectableTypes = new Set([
                            'text',
                            'search',
                            'url',
                            'tel',
                            'password',
                            'email',
                            'number',
                        ]);

                        if (textSelectableTypes.has(focusCandidate.type))
                        {
                            const textLength = focusCandidate.value.length;
                            focusCandidate.setSelectionRange(textLength, textLength);
                        }
                    }
                });
            };

            focusTarget(targetElement);

            const updatePosition = (): void =>
            {
                const nextTarget = document.getElementById(activeCoachmark.targetId);
                const bannerElement = tutorialBannerRef.current;
                const arrowElement = tutorialArrowRef.current;

                if (!nextTarget || !bannerElement)
                {
                    return;
                }

                const rect = nextTarget.getBoundingClientRect();
                const bannerWidth = bannerElement.offsetWidth || 560;
                const bannerHeight = bannerElement.offsetHeight || 180;
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const horizontalPadding = 24;
                const verticalPadding = 20;
                const arrowGap = 44;
                const left = Math.min(
                    Math.max(rect.left + (rect.width / 2) - (bannerWidth / 2), horizontalPadding),
                    Math.max(horizontalPadding, viewportWidth - bannerWidth - horizontalPadding),
                );
                const placeAbove = rect.top > bannerHeight + 90;
                const top = placeAbove
                    ? Math.max(verticalPadding, rect.top - bannerHeight - arrowGap)
                    : Math.min(viewportHeight - bannerHeight - verticalPadding, rect.bottom + arrowGap);
                const arrowLeft = Math.min(
                    Math.max((rect.left + (rect.width / 2)) - left, 30),
                    Math.max(30, bannerWidth - 30),
                );

                bannerElement.style.top = `${top}px`;
                bannerElement.style.left = `${left}px`;
                bannerElement.style.visibility = 'visible';

                if (arrowElement)
                {
                    arrowElement.style.left = `${arrowLeft}px`;
                }

                setTutorialPosition(
                    {
                        top,
                        left,
                        arrowLeft,
                        placement: placeAbove ? 'top' : 'bottom',
                    },
                );
            };

            animationFrameId = window.requestAnimationFrame(updatePosition);

            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);

            return () =>
            {
                window.cancelAnimationFrame(animationFrameId);
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition, true);
            };
        },
        [activeCoachmark],
    );

    async function handleContinueWorkspace(event: React.FormEvent): Promise<void>
    {
        event.preventDefault();
        setWorkspaceSubmitted(true);
        setWorkspaceError(null);

        if (!orgName.trim())
        {
            return;
        }

        setWorkspaceSaving(true);

        try
        {
            const organization = await createOrganization({
                name: orgName.trim(),
            });

            setCreatedOrganizationId(organization.id);

            const existingOrganizations = Array.isArray(user?.organizations)
                ? user.organizations.filter((item) => item.id !== organization.id)
                : [];

            updateUser({
                role: 'admin',
                organization_id: organization.id,
                organization_name: organization.name,
                organization_role: 'admin',
                active_organization_id: organization.id,
                organizations: [
                    { id: organization.id, name: organization.name, role: 'admin' },
                    ...existingOrganizations,
                ],
            });

            markOnboardingOrganizationCreated(getGuidedTourStepIndexById('billing-decision'));
            navigate('/billing?guidedTour=1', { replace: true });
        }
        catch (err: any)
        {
            const detail =
                err?.response?.data?.detail ||
                err?.message ||
                'Failed to create your workspace.';
            setWorkspaceError(detail);
        }
        finally
        {
            setWorkspaceSaving(false);
        }
    }

    async function handleContinueProject(event: React.FormEvent): Promise<void>
    {
        event.preventDefault();
        setProjectSubmitted(true);
        setProjectError(null);

        const requiresAzureConfig = projectType === 'azure_devops';

        if (
            !projectName.trim() ||
            (requiresAzureConfig && (!azureOrg.trim() || !azureProject.trim() || !azureTeam.trim() || !azurePAT.trim()))
        )
        {
            return;
        }

        setProjectSaving(true);

        try
        {
            const payload = {
                name: projectName.trim(),
                domain_description: domainDescription.trim() || undefined,
                project_type: projectType,
                azure_devops_org: requiresAzureConfig ? azureOrg.trim() : undefined,
                azure_devops_project: requiresAzureConfig ? azureProject.trim() : undefined,
                azure_devops_team: requiresAzureConfig ? azureTeam.trim() : undefined,
                azure_devops_pat: requiresAzureConfig ? azurePAT.trim() : undefined,
            };

            const project = createdProject
                ? await updateProject(createdProject.id, payload)
                : await createProject(payload);

            setCreatedProject(project);
            setStep(3);
        }
        catch (err: any)
        {
            const detail =
                err?.response?.data?.detail ||
                err?.message ||
                'Failed to create your project.';
            setProjectError(detail);
        }
        finally
        {
            setProjectSaving(false);
        }
    }

    function mergeSelectedRequirementFiles(incoming: File[]): void
    {
        const unsupportedFiles = incoming.filter((file) => !REQUIREMENT_ACCEPTED_EXTENSIONS.has(`.${file.name.split('.').pop()?.toLowerCase() || ''}`));
        if (unsupportedFiles.length > 0)
        {
            setSourceError(`Unsupported file type: ${unsupportedFiles.map((file) => file.name).join(', ')}`);
            return;
        }

        const emptyFiles = incoming.filter((file) => file.size === 0);
        if (emptyFiles.length > 0)
        {
            setSourceError(
                emptyFiles.length === 1
                    ? `"${emptyFiles[0].name}" is empty and cannot be uploaded.`
                    : `${emptyFiles.length} empty files cannot be uploaded.`,
            );
            return;
        }

        const oversizedFiles = incoming.filter((file) => file.size > REQUIREMENT_MAX_FILE_BYTES);
        if (oversizedFiles.length > 0)
        {
            setSourceError(`Each file must be 25 MB or smaller. Remove: ${oversizedFiles.map((file) => file.name).join(', ')}`);
            return;
        }

        if (incoming.length === 0)
        {
            return;
        }

        setSelectedRequirementFiles((current) =>
        {
            const existingKeys = new Set(current.map((file) => `${file.name}-${file.size}`));
            const uniqueFiles = incoming.filter((file) => !existingKeys.has(`${file.name}-${file.size}`));
            return [...current, ...uniqueFiles];
        });
    }

    function handleRequirementFilesChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        mergeSelectedRequirementFiles(Array.from(event.target.files ?? []));
        setDocumentFeedback(null);
        if (event.target.value)
        {
            event.target.value = '';
        }
    }

    function handleRemoveSelectedRequirementFile(index: number): void
    {
        setSelectedRequirementFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
    }

    async function handleUploadDocuments(event: React.FormEvent): Promise<void>
    {
        event.preventDefault();
        setSourceError(null);
        setDocumentFeedback(null);

        if (!createdProject?.id)
        {
            setSourceError('Create your project before uploading documents.');
            return;
        }

        if (selectedRequirementFiles.length === 0)
        {
            setSourceError('Select at least one requirement document to upload.');
            return;
        }

        const emptyFile = selectedRequirementFiles.find((file) => file.size === 0);
        if (emptyFile)
        {
            setSourceError(`"${emptyFile.name}" is empty and cannot be uploaded.`);
            return;
        }

        setUploadingDocuments(true);

        try
        {
            const nextDocuments = await uploadRequirementDocuments(
                createdProject.id,
                selectedRequirementFiles,
                { notes: documentNotes.trim() || undefined },
            );
            setDocuments(nextDocuments);
            setSelectedRequirementFiles([]);
            setDocumentNotes('');
            setDocumentFeedback(
                {
                    tone: 'success',
                    text: `${nextDocuments.length} requirement document${nextDocuments.length === 1 ? '' : 's'} available in this project.`,
                },
            );
        }
        catch (err: any)
        {
            setSourceError(formatApiError(err, 'Failed to upload requirement documents.'));
        }
        finally
        {
            setUploadingDocuments(false);
        }
    }

    function handleContinueSources(): void
    {
        setSourceError(null);

        if (documents.length === 0)
        {
            setSourceError('Upload at least one requirement document before moving on.');
            return;
        }

        setJobError(null);
        setStep(4);
    }

    async function handleRunFirstJob(): Promise<void>
    {
        if (!createdProject?.id)
        {
            setJobError('Create your project before running Test Case Creation.');
            return;
        }

        if (readyDocuments.length === 0)
        {
            setJobError('At least one uploaded document must finish processing before the job can start.');
            return;
        }

        if (quota && !quota.may_run_job)
        {
            setJobError(quota.block_reason || 'Your organization cannot run a new job right now.');
            return;
        }

        setJobSubmitting(true);
        setJobError(null);
        setJobFeedback(null);

        try
        {
            const createdJob = await queueRequirementGeneration(
                createdProject.id,
                {
                    document_ids: readyDocuments.map((document) => document.id),
                    include_artifacts:
                    {
                        requirements: includeRequirements,
                        test_cases: includeTestCases,
                    },
                },
            );

            setJob(createdJob);
            setJobFeedback(
                {
                    tone: 'success',
                    text: 'Test Case Creation is queued and will run against the ready documents in this project.',
                },
            );
            await loadQuota({ silent: true });
        }
        catch (err: any)
        {
            const detail =
                err?.response?.data?.detail ||
                err?.message ||
                'Failed to queue Test Case Creation.';
            setJobError(detail);
        }
        finally
        {
            setJobSubmitting(false);
        }
    }

    function handleOpenProject(): void
    {
        if (!createdProject?.id)
        {
            return;
        }

        navigate(`/projects/${createdProject.id}`, { replace: true });
    }

    function handleViewResults(): void
    {
        if (!createdProject?.id || !job?.id)
        {
            return;
        }

        navigate(projectTestCasesRoute(createdProject.id, { jobId: job.id }));
    }

    function handleGoToDashboard(): void
    {
        navigate('/dashboard', { replace: true });
    }

    /* ---------- Render helpers ---------- */

    function renderStepHeader(): React.ReactNode
    {
        const totalSteps = 4;
        const stepLabels: Record<OnboardingStep, string> = {
            1: 'Workspace Setup',
            2: 'Create Project',
            3: 'Add Sources',
            4: 'Generate your Test Cases',
        };

        return (
            <div className="ps-step-header">
                <span className="ps-step-label">STEP {step} OF {totalSteps}</span>
                <span className="ps-step-badge">{stepLabels[step]}</span>
                <div className="ps-progress-track">
                    <div className={`ps-progress-fill ps-progress-fill--step-${step}`} />
                </div>
            </div>
        );
    }

    function renderInlineMessage(message: InlineMessage | null): React.ReactNode
    {
        if (!message)
        {
            return null;
        }

        return (
            <div className={getMessageClassName(message.tone)}>
                {message.text}
            </div>
        );
    }

    function renderCoachmark(): React.ReactNode
    {
        if (!activeCoachmark)
        {
            return null;
        }

        return (
            <>
                <div className="ps-tutorial-layer" aria-hidden="true">
                    <div className="ps-tutorial-backdrop" />
                </div>

                <div
                    ref={tutorialBannerRef}
                    className={`ps-tutorial-banner ps-tutorial-banner--${tutorialPosition?.placement ?? 'bottom'}`}
                    aria-live="polite"
                >
                    <div
                        ref={tutorialArrowRef}
                        className={`ps-tutorial-arrow ps-tutorial-arrow--${tutorialPosition?.placement ?? 'bottom'}`}
                        aria-hidden="true"
                    >
                        <span className="ps-tutorial-arrow-line" />
                        <span className="ps-tutorial-arrow-head" />
                    </div>

                    <h3 className="ps-tutorial-title">{activeCoachmark.title}</h3>
                    <p className="ps-tutorial-body">{activeCoachmark.body}</p>

                    {tutorialBlockMessage && (
                        <div className="ps-tutorial-gate">
                            {tutorialBlockMessage}
                        </div>
                    )}

                    <div className="ps-tutorial-actions">
                        <button
                            type="button"
                            className="ps-tutorial-action ps-tutorial-action--ghost"
                            onClick={() => updateCoachmarkIndex(currentTutorialIndex - 1)}
                            disabled={isFirstCoachmark}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            className="ps-tutorial-action ps-tutorial-action--ghost"
                            onClick={() => setTutorialVisible(false)}
                        >
                            Hide guide
                        </button>
                        {!isLastCoachmark && !shouldHideNextTutorialTip && (
                            <button
                                type="button"
                                className="ps-tutorial-action ps-tutorial-action--primary"
                                onClick={() => updateCoachmarkIndex(currentTutorialIndex + 1)}
                                disabled={isTutorialUploadBlocked}
                            >
                                Next tip
                            </button>
                        )}
                    </div>
                </div>
            </>
        );
    }

    function renderDocumentList(emptyMessage: string): React.ReactNode
    {
        if (documents.length === 0)
        {
            return <p className="ps-empty-note">{emptyMessage}</p>;
        }

        return (
            <div className="ps-doc-list">
                {documents.map((document) => (
                    <div key={document.id} className="ps-doc-row">
                        <div className="ps-doc-icon">
                            <FileEarmarkTextFill size={16} />
                        </div>
                        <div className="ps-doc-meta">
                            <div className="ps-doc-name">{document.original_filename}</div>
                            <div className="ps-doc-subtitle">
                                {formatFileSize(document.file_size)}
                                {document.extracted_token_count != null
                                    ? ` • ${document.extracted_token_count.toLocaleString()} tokens`
                                    : ''}
                            </div>
                        </div>
                        <span className={getDocumentStatusClass(document.status)}>
                            {document.status}
                        </span>
                    </div>
                ))}
            </div>
        );
    }

    function renderWorkspaceStep(): React.ReactNode
    {
        return (
            <div className="ps-step-content">
                <div className="ps-step-panel ps-step-panel--full">
                    <h2 className="ps-heading">Tell us about your organization</h2>
                    <p className="ps-subtitle">
                        This creates your workspace and unlocks the free starter quota for the first project job.
                    </p>

                    <div className="ps-note-strip">
                        <strong>What happens next:</strong>
                        You will review free quota and subscription options before creating your first project.
                    </div>

                    {workspaceError && (
                        <div className="auth-server-error">{workspaceError}</div>
                    )}

                    <Form onSubmit={handleContinueWorkspace} noValidate>
                        <div
                            id="onboarding-org-name"
                            className={getTutorialTargetClassName('onboarding-org-name', 'auth-form-group')}
                        >
                            <Form.Label className="auth-label">Organization Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={orgName}
                                onChange={(event) => setOrgName(event.target.value)}
                                placeholder="Enter organization name"
                                isInvalid={workspaceSubmitted && !orgName.trim()}
                                isValid={workspaceSubmitted && !!orgName.trim()}
                            />
                        </div>

                        <div className="ps-footer-row">
                            <button
                                type="submit"
                                id="onboarding-workspace-continue"
                                className={getTutorialTargetClassName('onboarding-workspace-continue', 'auth-submit-btn ps-inline-submit')}
                                disabled={workspaceSaving}
                            >
                                {workspaceSaving
                                    ? <Spinner as="span" animation="border" size="sm" />
                                    : 'Continue to Billing →'
                                }
                            </button>
                        </div>
                    </Form>
                </div>
            </div>
        );
    }

    function renderProjectStep(): React.ReactNode
    {
        const simpleFolderColor = projectType === 'simple' ? '#93c5fd' : '#6b7280';
        const azureIconFill = projectType === 'azure_devops' ? '#ffffff' : '#0078D4';

        return (
            <div className="ps-step-content">
                <div className="ps-step-panel ps-step-panel--full">
                    <h2 className="ps-heading">Create your first project</h2>
                    <p className="ps-subtitle">
                        Set the project context now so onboarding can attach your source material and run Test Case Creation against a real workspace.
                    </p>

                    <div className="ps-note-strip">
                        <strong>{orgName || 'Your workspace'}</strong>
                        {' '}is ready. The next step creates the project record the onboarding uploads and job will use.
                    </div>

                    {projectError && (
                        <div className="auth-server-error">{projectError}</div>
                    )}

                    <Form onSubmit={handleContinueProject} noValidate>
                        <div className="ps-form-grid">
                            <Form.Group
                                id="onboarding-project-name"
                                className={getTutorialTargetClassName('onboarding-project-name', 'ps-field-span-full')}
                            >
                                <Form.Label className="project-create-label">Project Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Name your project"
                                    value={projectName}
                                    onChange={(event) => setProjectName(event.target.value)}
                                    className="project-create-input"
                                    isInvalid={projectSubmitted && !projectName.trim()}
                                />
                            </Form.Group>

                            <Form.Group
                                id="onboarding-domain-description"
                                className={getTutorialTargetClassName('onboarding-domain-description', 'ps-field-span-full')}
                            >
                                <Form.Label className="project-create-label">Domain Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="Describe the business domain, product surface, and workflows you want the generated assets to reflect"
                                    value={domainDescription}
                                    onChange={(event) => setDomainDescription(event.target.value)}
                                    className="project-create-input"
                                />
                            </Form.Group>

                            <Form.Group
                                id="onboarding-project-type"
                                className={getTutorialTargetClassName('onboarding-project-type', 'ps-field-span-full')}
                            >
                                <Form.Label className="project-create-label">Project Type</Form.Label>
                                <div className="project-type-cards" role="radiogroup" aria-label="Project type">
                                    <ProjectTypeCard
                                        cardId="profile-setup-simple"
                                        selected={projectType === 'simple'}
                                        onSelect={() => setProjectType('simple')}
                                        label="Simple Project"
                                        icon={<FolderFill size={28} color={simpleFolderColor} />}
                                    />
                                    <ProjectTypeCard
                                        cardId="profile-setup-azure"
                                        selected={projectType === 'azure_devops'}
                                        onSelect={() => setProjectType('azure_devops')}
                                        label="Azure DevOps"
                                        icon={<AzureDevOpsIcon fill={azureIconFill} />}
                                    />
                                </div>
                            </Form.Group>
                        </div>

                        {projectType === 'azure_devops' && (
                            <div className="azure-devops-config ps-step-section-gap">
                                <h6 className="azure-devops-config-title">Azure DevOps Configuration</h6>
                                <div className="ps-form-grid">
                                    <Form.Group
                                        id="onboarding-azure-org"
                                        className={getTutorialTargetClassName('onboarding-azure-org', 'mb-0')}
                                    >
                                        <Form.Label className="project-create-label">Organization</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="my-organization"
                                            value={azureOrg}
                                            onChange={(event) => setAzureOrg(event.target.value)}
                                            className="project-create-input"
                                            isInvalid={projectSubmitted && !azureOrg.trim()}
                                        />
                                    </Form.Group>

                                    <Form.Group
                                        id="onboarding-azure-project"
                                        className={getTutorialTargetClassName('onboarding-azure-project', 'mb-0')}
                                    >
                                        <Form.Label className="project-create-label">Project</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="MyProject"
                                            value={azureProject}
                                            onChange={(event) => setAzureProject(event.target.value)}
                                            className="project-create-input"
                                            isInvalid={projectSubmitted && !azureProject.trim()}
                                        />
                                    </Form.Group>

                                    <Form.Group
                                        id="onboarding-azure-team"
                                        className={getTutorialTargetClassName('onboarding-azure-team', 'mb-0')}
                                    >
                                        <Form.Label className="project-create-label">Team</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="MyTeam"
                                            value={azureTeam}
                                            onChange={(event) => setAzureTeam(event.target.value)}
                                            className="project-create-input"
                                            isInvalid={projectSubmitted && !azureTeam.trim()}
                                        />
                                    </Form.Group>

                                    <Form.Group
                                        id="onboarding-azure-pat"
                                        className={getTutorialTargetClassName('onboarding-azure-pat', 'mb-0')}
                                    >
                                        <Form.Label className="project-create-label">Personal Access Token</Form.Label>
                                        <Form.Control
                                            type="password"
                                            placeholder="Enter your PAT"
                                            value={azurePAT}
                                            onChange={(event) => setAzurePAT(event.target.value)}
                                            className="project-create-input"
                                            isInvalid={projectSubmitted && !azurePAT.trim()}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        )}

                        <div className="ps-footer-row ps-footer-row--between">
                            <button
                                type="button"
                                className="ps-back-link ps-back-link--button"
                                onClick={() => setStep(1)}
                            >
                                ← Back
                            </button>
                            <button
                                type="submit"
                                id="onboarding-project-continue"
                                className={getTutorialTargetClassName('onboarding-project-continue', 'auth-submit-btn ps-inline-submit')}
                                disabled={projectSaving}
                            >
                                {projectSaving
                                    ? <Spinner as="span" animation="border" size="sm" />
                                    : createdProject ? 'Update Project →' : 'Create Project →'
                                }
                            </button>
                        </div>
                    </Form>
                </div>
            </div>
        );
    }

    function renderSourcesStep(): React.ReactNode
    {
        return (
            <div className="ps-step-content">
                <div className="ps-step-panel ps-step-panel--full">
                    <h2 className="ps-heading">Add requirement documents</h2>
                    <p className="ps-subtitle">
                        Requirement documents are the source material used for the first free-quota job.
                    </p>
                </div>

                {documentFeedback && renderInlineMessage(documentFeedback)}
                {sourceError && <div className="auth-server-error">{sourceError}</div>}

                <div className="ps-surface">
                    <div className="ps-surface-head">
                        <div>
                            <h3 className="ps-surface-title">Requirement Documents</h3>
                            <p className="ps-surface-subtitle">
                                Supported formats: TXT, Markdown, CSV, PDF, DOCX, XLSX.
                            </p>
                        </div>
                    </div>

                    <Form onSubmit={handleUploadDocuments} noValidate>
                        <Form.Group
                            id="onboarding-document-files"
                            className={getTutorialTargetClassName('onboarding-document-files', 'ps-step-section-gap')}
                        >
                            <Form.Label className="project-create-label">Select Files</Form.Label>
                            <Form.Control
                                type="file"
                                multiple
                                accept={REQUIREMENT_ACCEPTED_TYPES}
                                onChange={handleRequirementFilesChange}
                                disabled={uploadingDocuments}
                                className="project-create-input"
                            />
                        </Form.Group>

                        {selectedRequirementFiles.length > 0 && (
                            <div className="ps-selected-file-list">
                                {selectedRequirementFiles.map((file, index) => (
                                    <div key={`${file.name}-${file.size}`} className="ps-selected-file-item">
                                        <span className="ps-selected-file-name">{file.name} ({formatFileSize(file.size)})</span>
                                        <button
                                            type="button"
                                            className="ps-selected-file-remove"
                                            onClick={() => handleRemoveSelectedRequirementFile(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <Form.Group
                            id="onboarding-document-notes"
                            className={getTutorialTargetClassName('onboarding-document-notes', 'ps-step-section-gap')}
                        >
                            <Form.Label className="project-create-label">Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={documentNotes}
                                onChange={(event) => setDocumentNotes(event.target.value)}
                                className="project-create-input"
                                placeholder="Optional context for the uploaded requirement material"
                                disabled={uploadingDocuments}
                            />
                        </Form.Group>

                        <div className="ps-inline-actions ps-inline-actions--upload">
                            <button
                                id="onboarding-document-upload"
                                type="submit"
                                className={getTutorialTargetClassName('onboarding-document-upload', 'ps-secondary-btn')}
                                disabled={uploadingDocuments || selectedRequirementFiles.length === 0}
                            >
                                {uploadingDocuments
                                    ? <Spinner as="span" animation="border" size="sm" />
                                    : <><CloudArrowUpFill size={14} /> Upload Documents</>
                                }
                            </button>
                        </div>
                    </Form>
                </div>

                <div className="ps-surface">
                    <div className="ps-surface-head">
                        <div>
                            <h3 className="ps-surface-title">Uploaded Requirement Documents</h3>
                            <p className="ps-surface-subtitle">
                                Extraction starts immediately after upload. You can continue once at least one document is uploaded.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="ps-ghost-btn"
                            onClick={() => createdProject?.id && void loadProjectDocuments(createdProject.id)}
                            disabled={documentsLoading || !createdProject?.id}
                        >
                            {documentsLoading
                                ? <Spinner as="span" animation="border" size="sm" />
                                : <><ArrowClockwise size={14} /> Refresh</>
                            }
                        </button>
                    </div>

                    {renderDocumentList('No requirement documents uploaded yet.')}
                </div>

                <div className="ps-footer-row ps-footer-row--between">
                    <button
                        type="button"
                        className="ps-back-link ps-back-link--button"
                        onClick={() => setStep(2)}
                    >
                        ← Back
                    </button>
                    <button
                        type="button"
                        id="onboarding-sources-continue"
                        className={getTutorialTargetClassName('onboarding-sources-continue', 'auth-submit-btn ps-inline-submit')}
                        onClick={handleContinueSources}
                    >
                        Continue to Test Case Creation →
                    </button>
                </div>
            </div>
        );
    }

    function renderRunStep(): React.ReactNode
    {
        const quotaBlocked = Boolean(quota && !quota.may_run_job);
        const showResultsButton = Boolean(job?.id && job.status.toLowerCase() === 'completed');

        return (
            <div className="ps-step-content">
                <div className="ps-step-panel ps-step-panel--full">
                    <h2 className="ps-heading">Run Test Case Creation on the free quota</h2>
                    <p className="ps-subtitle">
                        We will queue a requirement-generation job with the documents that are ready right now. Any late-arriving documents can be generated later from the project workspace.
                    </p>
                </div>

                {jobFeedback && renderInlineMessage(jobFeedback)}
                {jobError && <div className="auth-server-error">{jobError}</div>}
                {quotaError && <div className="auth-server-info">{quotaError}</div>}

                <div className="ps-summary-grid">
                    <div
                        id="onboarding-starter-quota"
                        className={getTutorialTargetClassName('onboarding-starter-quota', 'ps-surface ps-surface--accent')}
                    >
                        <div className="ps-surface-head">
                            <div>
                                <h3 className="ps-surface-title">Starter Quota</h3>
                                <p className="ps-surface-subtitle">
                                    {quota?.active_balance_source === 'free'
                                        ? 'Your organization is currently using the free starter balance.'
                                        : 'This organization can use the starter balance until it is exhausted.'
                                    }
                                </p>
                            </div>

                            <button
                                type="button"
                                className="ps-ghost-btn"
                                onClick={() => void loadQuota()}
                                disabled={quotaLoading}
                            >
                                {quotaLoading
                                    ? <Spinner as="span" animation="border" size="sm" />
                                    : <><ArrowClockwise size={14} /> Refresh</>
                                }
                            </button>
                        </div>

                        <div className="ps-metric-grid">
                            <div className="ps-metric-card">
                                <span className="ps-metric-label">Free Allocated</span>
                                <strong>{formatTokenCount(quota?.free_tokens_allocated)}</strong>
                            </div>
                            <div className="ps-metric-card">
                                <span className="ps-metric-label">Free Remaining</span>
                                <strong>{formatTokenCount(quota?.free_tokens_remaining)}</strong>
                            </div>
                            <div className="ps-metric-card">
                                <span className="ps-metric-label">Ready Documents</span>
                                <strong>{readyDocuments.length}</strong>
                            </div>
                        </div>

                        {quotaBlocked && (
                            <div className="ps-inline-note ps-inline-note--danger">
                                {quota?.block_reason || 'A new job cannot start until quota is available again.'}
                            </div>
                        )}
                    </div>

                    <div className="ps-surface">
                        <div className="ps-surface-head">
                            <div>
                                <h3 className="ps-surface-title">Onboarding Snapshot</h3>
                                <p className="ps-surface-subtitle">
                                    Everything in this checklist is already grounded in the real project you just created.
                                </p>
                            </div>
                        </div>

                        <div className="ps-checklist">
                            <div className="ps-checklist-row">
                                <div className="ps-checklist-icon"><Building size={16} /></div>
                                <div>
                                    <div className="ps-summary-title">Workspace</div>
                                    <div className="ps-summary-desc">{orgName}</div>
                                </div>
                            </div>
                            <div className="ps-checklist-row">
                                <div className="ps-checklist-icon"><FolderFill size={16} /></div>
                                <div>
                                    <div className="ps-summary-title">Project</div>
                                    <div className="ps-summary-desc">{projectName}</div>
                                </div>
                            </div>
                            <div className="ps-checklist-row">
                                <div className="ps-checklist-icon"><FileEarmarkTextFill size={16} /></div>
                                <div>
                                    <div className="ps-summary-title">Documents</div>
                                    <div className="ps-summary-desc">
                                        {documents.length} uploaded • {readyDocuments.length} ready • {pendingDocuments.length} processing
                                    </div>
                                </div>
                            </div>
                            <div className="ps-checklist-row">
                                <div className="ps-checklist-icon"><RocketTakeoffFill size={16} /></div>
                                <div>
                                    <div className="ps-summary-title">Test Case Creation</div>
                                    <div className="ps-summary-desc">
                                        {job ? `Status: ${job.status}` : 'Ready to queue from this step'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="ps-surface">
                    <div className="ps-surface-head">
                        <div>
                            <h3 className="ps-surface-title">Requirement Readiness</h3>
                            <p className="ps-surface-subtitle">
                                The job will use the documents marked ready. You can refresh while background extraction finishes.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="ps-ghost-btn"
                            onClick={() => createdProject?.id && void loadProjectDocuments(createdProject.id)}
                            disabled={documentsLoading || !createdProject?.id}
                        >
                            {documentsLoading
                                ? <Spinner as="span" animation="border" size="sm" />
                                : <><ArrowClockwise size={14} /> Refresh</>
                            }
                        </button>
                    </div>

                    {renderDocumentList('No requirement documents are available in this project yet.')}

                    {readyDocuments.length === 0 && pendingDocuments.length > 0 && (
                        <div className="ps-inline-note">
                            Your documents are still extracting. Stay on this screen and we will keep refreshing automatically until something is ready.
                        </div>
                    )}

                    {readyDocuments.length === 0 && pendingDocuments.length === 0 && failedDocuments.length > 0 && (
                        <div className="ps-inline-note ps-inline-note--danger">
                            Uploaded documents failed to process. Go back to the previous step and upload a new source file to continue onboarding.
                        </div>
                    )}
                </div>

                <div className="ps-surface">
                    <div className="ps-surface-head">
                        <div>
                            <h3 className="ps-surface-title">Run Options</h3>
                            <p className="ps-surface-subtitle">
                                Start with requirements only or generate requirements plus structured test cases together.
                            </p>
                        </div>
                    </div>

                    <div className="ps-run-options">
                        <div
                            id="onboarding-run-requirements-option"
                            className={getTutorialTargetClassName('onboarding-run-requirements-option', 'ps-run-check-wrap')}
                        >
                            <Form.Check
                                type="checkbox"
                                id="onboarding-include-requirements"
                                className="ps-run-check"
                                label="Extracted requirements"
                                checked={includeRequirements}
                                onChange={(event) =>
                                {
                                    const checked = event.currentTarget.checked;
                                    setIncludeRequirements(checked);
                                    if (!checked)
                                    {
                                        setIncludeTestCases(false);
                                    }
                                }}
                            />
                        </div>
                        <div
                            id="onboarding-run-testcases-option"
                            className={getTutorialTargetClassName('onboarding-run-testcases-option', 'ps-run-check-wrap')}
                        >
                            <Form.Check
                                type="checkbox"
                                id="onboarding-include-testcases"
                                className="ps-run-check"
                                label="Structured test cases"
                                checked={includeTestCases}
                                onChange={(event) =>
                                {
                                    const checked = event.currentTarget.checked;
                                    setIncludeTestCases(checked);
                                    if (checked)
                                    {
                                        setIncludeRequirements(true);
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {job && (
                        <div className="ps-job-card">
                            <div className="ps-job-head">
                                <div>
                                    <div className="ps-surface-title">Latest Job</div>
                                    <div className="ps-surface-subtitle">Job #{job.id}</div>
                                </div>
                                <span className={getJobStatusClass(job.status)}>{job.status}</span>
                            </div>

                            <progress
                                className="ps-job-progress-native"
                                value={resolveJobProgress(job)}
                                max={100}
                            />

                            <div className="ps-job-meta">
                                <span>{job.document_count} document{job.document_count === 1 ? '' : 's'}</span>
                                <span>{job.include_artifacts.test_cases ? 'Requirements + test cases' : 'Requirements only'}</span>
                                <span>{resolveJobProgress(job)}%</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="ps-footer-row ps-footer-row--between ps-footer-row--wrap">
                    <button
                        type="button"
                        className="ps-back-link ps-back-link--button"
                        onClick={() => setStep(3)}
                    >
                        ← Back
                    </button>

                    <div className="ps-inline-actions ps-inline-actions--end">
                        {!job && (
                            <button
                                type="button"
                                id="onboarding-run-job"
                                className={getTutorialTargetClassName('onboarding-run-job', 'auth-submit-btn ps-inline-submit')}
                                disabled={jobSubmitting || readyDocuments.length === 0 || quotaBlocked}
                                onClick={() => void handleRunFirstJob()}
                            >
                                {jobSubmitting
                                    ? <Spinner as="span" animation="border" size="sm" />
                                    : 'Generate Test Cases'
                                }
                            </button>
                        )}

                        {job && (
                            <>
                                {showResultsButton && (
                                    <button
                                        type="button"
                                        className="ps-secondary-btn"
                                        onClick={handleViewResults}
                                    >
                                        View Results
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="ps-secondary-btn"
                                    onClick={handleOpenProject}
                                >
                                    Open Project Workspace
                                </button>
                                <button
                                    type="button"
                                    className="ps-ghost-btn"
                                    onClick={handleGoToDashboard}
                                >
                                    Go to Dashboard
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (authLoading)
    {
        return (
            <div className="sp-page">
                <div className="auth-card ps-card">
                    <div className="ps-loading-state">
                        <Spinner animation="border" size="sm" />
                        <span>Preparing onboarding…</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="sp-page">
            <div className="auth-card ps-card">
                {renderStepHeader()}

                <div className="ps-welcome-row">
                    <div>
                        <p className="ps-eyebrow">Welcome, {firstName}</p>
                        <h1 className="ps-page-title">Let’s set up your first test workspace</h1>
                    </div>
                    <div className="ps-profile-actions">
                        <button
                            type="button"
                            className="ps-tour-toggle"
                            onClick={() => setTutorialVisible((current) => !current)}
                        >
                            {tutorialVisible ? 'Hide Guide' : 'Show Guide'}
                        </button>
                        <span className="ps-profile-pill">Signup Onboarding</span>
                    </div>
                </div>

                {step === 1 && renderWorkspaceStep()}
                {step === 2 && renderProjectStep()}
                {step === 3 && renderSourcesStep()}
                {step === 4 && renderRunStep()}
            </div>

            {renderCoachmark()}
        </div>
    );
};

export default ProfileSetupNew;
